/**
 * Boutique à l’image du producteur — modèles, paliers et lien de pub.
 *
 * Un client qui arrive par le lien de la ferme n’est pas sur le marché
 * Champ’Table : il est chez eux. La peau surcharge les jetons `--ct-*`.
 * Le crescendo commence au palier 4 (modèle) et va jusqu’au 10 (marque blanche).
 */

export const SHOP_THEME_TIER_LEVEL = 4;
export const SHOP_ACCENT_TIER_LEVEL = 5;
export const SHOP_HERO_TIER_LEVEL = 5;
export const SHOP_LOGO_TIER_LEVEL = 6;
export const SHOP_MOTION_TIER_LEVEL = 7;
export const SHOP_CHROME_TIER_LEVEL = 8;
export const SHOP_TYPED_DOMAIN_TIER_LEVEL = 8;
export const SHOP_MONTHLY_TIER_LEVEL = 9;
export const SHOP_WHITE_LABEL_TIER_LEVEL = 10;
export const SHOP_CUSTOM_DOMAIN_TIER_LEVEL = 10;

export type ShopTemplateId = 'potager' | 'verger' | 'fromagerie' | 'four' | 'cave' | 'ferme';
export type ShopMotion = 'none' | 'soft' | 'vif';
export type ShopDomainKind = 'path' | 'typed_subdomain' | 'custom';
export type ShopDomainStatus = 'active' | 'requested' | 'rejected';

export interface ShopAccent {
  readonly id: string;
  readonly label: string;
  readonly value: string;
}

export interface ShopTemplate {
  readonly id: ShopTemplateId;
  readonly label: string;
  readonly pitch: string;
  readonly categories: readonly string[];
  readonly tokens: Readonly<Record<string, string>>;
  readonly accents: readonly ShopAccent[];
}

export const SHOP_TEMPLATES: readonly ShopTemplate[] = [
  {
    id: 'potager',
    label: 'Potager',
    pitch: 'Verts de cageot, terre fraîche — légumes et herbes.',
    categories: ['legumes'],
    tokens: {
      '--ct-vert-cageot': '#2f5233',
      '--ct-vert-cageot-light': '#547a58',
      '--ct-page': '#e7edd9',
      '--ct-kraft': '#f3f0e4',
      '--ct-kraft-dark': '#d5d0b8',
      '--ct-terre-cuite': '#8a6230',
    },
    accents: [
      { id: 'vert', label: 'Cageot', value: '#2f5233' },
      { id: 'sauge', label: 'Sauge', value: '#547a58' },
      { id: 'feuille', label: 'Feuille', value: '#3d6b32' },
    ],
  },
  {
    id: 'verger',
    label: 'Verger',
    pitch: 'Or de récolte et lumière d’été — fruits, jus, confitures.',
    categories: ['fruits'],
    tokens: {
      '--ct-vert-cageot': '#6b4a1e',
      '--ct-vert-cageot-light': '#a67c3a',
      '--ct-page': '#f4ead4',
      '--ct-kraft': '#fbf3e3',
      '--ct-kraft-dark': '#e4d3b0',
      '--ct-or-recolte': '#d9a441',
      '--ct-terre-cuite': '#c1552c',
    },
    accents: [
      { id: 'or', label: 'Récolte', value: '#d9a441' },
      { id: 'abricot', label: 'Abricot', value: '#c1552c' },
      { id: 'feuille', label: 'Feuillage', value: '#6b8f3d' },
    ],
  },
  {
    id: 'fromagerie',
    label: 'Fromagerie',
    pitch: 'Crème, pierre, cave — laitier et crèmerie.',
    categories: ['laitier'],
    tokens: {
      '--ct-vert-cageot': '#5c5346',
      '--ct-vert-cageot-light': '#8a7d6b',
      '--ct-page': '#f4efe4',
      '--ct-kraft': '#fbf8f1',
      '--ct-kraft-dark': '#ddd4c4',
      '--ct-terre-cuite': '#a67c52',
    },
    accents: [
      { id: 'creme', label: 'Crème', value: '#a67c52' },
      { id: 'pierre', label: 'Pierre', value: '#5c5346' },
      { id: 'cave', label: 'Cave', value: '#3d342c' },
    ],
  },
  {
    id: 'four',
    label: 'Four',
    pitch: 'Farine, chaleur, terre cuite — pain et transformé.',
    categories: ['transforme'],
    tokens: {
      '--ct-vert-cageot': '#9c4322',
      '--ct-vert-cageot-light': '#c1552c',
      '--ct-page': '#f0e4d4',
      '--ct-kraft': '#f8efe4',
      '--ct-kraft-dark': '#e0d0bc',
      '--ct-terre-cuite': '#c1552c',
    },
    accents: [
      { id: 'terre', label: 'Terre cuite', value: '#c1552c' },
      { id: 'pain', label: 'Croûte', value: '#9c4322' },
      { id: 'ble', label: 'Blé', value: '#d9a441' },
    ],
  },
  {
    id: 'cave',
    label: 'Cave',
    pitch: 'Sombre, étiquette, verre — bouteilles et bocaux.',
    categories: ['transforme'],
    tokens: {
      '--ct-vert-cageot': '#3d2e38',
      '--ct-vert-cageot-light': '#6b4a58',
      '--ct-page': '#ebe4dc',
      '--ct-kraft': '#f4eee6',
      '--ct-kraft-dark': '#d4cbc0',
      '--ct-terre-cuite': '#7a2e3a',
    },
    accents: [
      { id: 'lie', label: 'Lie de vin', value: '#7a2e3a' },
      { id: 'bouchon', label: 'Bouchon', value: '#6b4a1e' },
      { id: 'ardoise', label: 'Ardoise', value: '#3d2e38' },
    ],
  },
  {
    id: 'ferme',
    label: 'Ferme',
    pitch: 'Bois, cour, kraft — mixte ou autre production.',
    categories: ['autre', 'fruits', 'legumes', 'laitier', 'transforme'],
    tokens: {
      '--ct-vert-cageot': '#2f5233',
      '--ct-vert-cageot-light': '#547a58',
      '--ct-page': '#e4ddc9',
      '--ct-kraft': '#efe8d8',
      '--ct-kraft-dark': '#ddd4bd',
    },
    accents: [
      { id: 'cageot', label: 'Cageot', value: '#2f5233' },
      { id: 'bois', label: 'Bois', value: '#6b4a1e' },
      { id: 'or', label: 'Or', value: '#d9a441' },
    ],
  },
];

export const SHOP_MOTIONS: readonly { readonly id: ShopMotion; readonly label: string }[] = [
  { id: 'none', label: 'Aucun' },
  { id: 'soft', label: 'Doux' },
  { id: 'vif', label: 'Vif' },
];

const RESERVED_SLUGS = new Set([
  'chez',
  'vendre',
  'producteur',
  'catalog',
  'connexion',
  'panier',
  'profil',
  'design',
  'me',
]);

export interface ShopThemeInput {
  readonly slug: string;
  readonly template: string;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: string;
  readonly domainKind: string;
  readonly customHost: string | null;
}

export interface ShopTheme {
  readonly slug: string;
  readonly template: ShopTemplateId;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: ShopMotion;
  readonly seasonPack: string | null;
  readonly domainKind: ShopDomainKind;
  readonly domainStatus: ShopDomainStatus;
  readonly customHost: string | null;
}

export interface ShopCapabilities {
  readonly canTheme: boolean;
  readonly canAccent: boolean;
  readonly canHero: boolean;
  readonly canLogo: boolean;
  readonly canMotion: boolean;
  readonly canChrome: boolean;
  readonly canTypedSubdomain: boolean;
  readonly canMonthlySkin: boolean;
  readonly canWhiteLabel: boolean;
  readonly canCustomDomain: boolean;
}

export function shopCapabilities(tierLevel: number): ShopCapabilities {
  return {
    canTheme: tierLevel >= SHOP_THEME_TIER_LEVEL,
    canAccent: tierLevel >= SHOP_ACCENT_TIER_LEVEL,
    canHero: tierLevel >= SHOP_HERO_TIER_LEVEL,
    canLogo: tierLevel >= SHOP_LOGO_TIER_LEVEL,
    canMotion: tierLevel >= SHOP_MOTION_TIER_LEVEL,
    canChrome: tierLevel >= SHOP_CHROME_TIER_LEVEL,
    canTypedSubdomain: tierLevel >= SHOP_TYPED_DOMAIN_TIER_LEVEL,
    canMonthlySkin: tierLevel >= SHOP_MONTHLY_TIER_LEVEL,
    canWhiteLabel: tierLevel >= SHOP_WHITE_LABEL_TIER_LEVEL,
    canCustomDomain: tierLevel >= SHOP_CUSTOM_DOMAIN_TIER_LEVEL,
  };
}

export function shopTemplateById(id: string): ShopTemplate | undefined {
  return SHOP_TEMPLATES.find((entry) => entry.id === id);
}

export function suggestedTemplates(categories: readonly string[]): readonly ShopTemplateId[] {
  const ids: ShopTemplateId[] = [];
  for (const template of SHOP_TEMPLATES) {
    if (template.categories.some((category) => categories.includes(category))) {
      ids.push(template.id);
    }
  }
  if (!ids.includes('ferme')) {
    ids.push('ferme');
  }
  return ids;
}

export function slugifyShopName(name: string): string {
  const ascii = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return ascii.length >= 3 ? ascii : 'boutique';
}

export function isShopSlug(value: string): boolean {
  return (
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/u.test(value) &&
    value.length >= 3 &&
    value.length <= 48 &&
    !RESERVED_SLUGS.has(value)
  );
}

export type ShopThemeVerdict =
  | { readonly ok: true; readonly value: Omit<ShopTheme, 'seasonPack' | 'domainStatus'> }
  | { readonly ok: false; readonly code: string; readonly message: string };

export function validateShopTheme(input: ShopThemeInput, tierLevel: number): ShopThemeVerdict {
  const caps = shopCapabilities(tierLevel);
  if (!caps.canTheme) {
    return {
      ok: false,
      code: 'SHOP_TIER',
      message: 'La boutique à votre image s’ouvre au palier 4 (Producteur Établi).',
    };
  }

  const slug = input.slug.trim().toLowerCase();
  if (!isShopSlug(slug)) {
    return {
      ok: false,
      code: 'SLUG_INVALID',
      message: 'Le lien public : 3 à 48 lettres, chiffres ou tirets, sans accent.',
    };
  }

  const template = shopTemplateById(input.template);
  if (template === undefined) {
    return { ok: false, code: 'TEMPLATE_INVALID', message: 'Choisissez un modèle de boutique.' };
  }

  let accent: string | null = null;
  if (caps.canAccent && input.accent !== null && input.accent.length > 0) {
    const match = template.accents.find((entry) => entry.id === input.accent);
    if (match === undefined) {
      return { ok: false, code: 'ACCENT_INVALID', message: 'Cette teinte n’appartient pas au modèle.' };
    }
    accent = match.id;
  }

  const heroUrl = caps.canHero ? blankToNull(input.heroUrl) : null;
  const logoUrl = caps.canLogo ? blankToNull(input.logoUrl) : null;

  let motion: ShopMotion = 'none';
  if (caps.canMotion) {
    if (input.motion !== 'none' && input.motion !== 'soft' && input.motion !== 'vif') {
      return { ok: false, code: 'MOTION_INVALID', message: 'Choisissez un mouvement : aucun, doux ou vif.' };
    }
    motion = input.motion;
  }

  let domainKind: ShopDomainKind = 'path';
  let customHost: string | null = null;
  if (input.domainKind === 'typed_subdomain') {
    if (!caps.canTypedSubdomain) {
      return {
        ok: false,
        code: 'DOMAIN_TIER',
        message: 'Le sous-domaine métier s’ouvre au palier 8, en extra. Les noms de domaine ne sont pas encore choisis.',
      };
    }
    domainKind = 'typed_subdomain';
  } else if (input.domainKind === 'custom') {
    if (!caps.canCustomDomain) {
      return {
        ok: false,
        code: 'DOMAIN_TIER',
        message: 'Votre nom de domaine (ex. gaec-alpines.fr) s’ouvre au palier 10, en extra ++. Nous l’enregistrons dans Route 53.',
      };
    }
    const host = (input.customHost ?? '').trim().toLowerCase();
    if (!isCustomHost(host)) {
      return {
        ok: false,
        code: 'HOST_INVALID',
        message: 'Indiquez le nom à enregistrer (ex. gaec-alpines.fr).',
      };
    }
    domainKind = 'custom';
    customHost = host;
  } else if (input.domainKind !== 'path' && input.domainKind.length > 0) {
    return { ok: false, code: 'DOMAIN_KIND_INVALID', message: 'Type d’adresse inconnu.' };
  }

  return {
    ok: true,
    value: {
      slug,
      template: template.id,
      accent,
      heroUrl,
      logoUrl,
      motion,
      domainKind,
      customHost,
    },
  };
}

const SEASON_PACKS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  primeurs: { '--ct-or-recolte': '#7a9b3e', '--ct-page': '#e8f0d8' },
  ete: { '--ct-or-recolte': '#e0b25a', '--ct-page': '#f4ead4' },
  vendanges: { '--ct-vert-cageot': '#5c2a38', '--ct-or-recolte': '#c9a227' },
  hivernage: { '--ct-page': '#e8e2d6', '--ct-kraft': '#f3eee4' },
};

export function shopCssVars(
  theme: Pick<ShopTheme, 'template' | 'accent' | 'seasonPack'>,
): Readonly<Record<string, string>> {
  const template = shopTemplateById(theme.template);
  if (template === undefined) {
    return {};
  }
  const vars = { ...template.tokens };
  if (theme.accent !== null) {
    const accent = template.accents.find((entry) => entry.id === theme.accent);
    if (accent !== undefined) {
      vars['--ct-vert-cageot'] = accent.value;
    }
  }
  if (theme.seasonPack !== null) {
    const pack = SEASON_PACKS[theme.seasonPack];
    if (pack !== undefined) {
      Object.assign(vars, pack);
    }
  }
  return vars;
}

export interface PublicShopSkin {
  readonly slug: string;
  readonly template: ShopTemplateId;
  readonly accent: string | null;
  readonly heroUrl: string | null;
  readonly logoUrl: string | null;
  readonly motion: ShopMotion;
  readonly seasonPack: string | null;
  readonly cssVars: Readonly<Record<string, string>>;
  readonly chrome: boolean;
  readonly whiteLabel: boolean;
}

export function toPublicShopSkin(theme: ShopTheme, tierLevel: number): PublicShopSkin {
  const caps = shopCapabilities(tierLevel);
  return {
    slug: theme.slug,
    template: theme.template,
    accent: theme.accent,
    heroUrl: theme.heroUrl,
    logoUrl: theme.logoUrl,
    motion: theme.motion,
    seasonPack: theme.seasonPack,
    cssVars: shopCssVars(theme),
    chrome: caps.canChrome,
    whiteLabel: caps.canWhiteLabel,
  };
}

export function shopPublicPath(slug: string): string {
  return `/chez/${encodeURIComponent(slug)}`;
}

/**
 * Apex métier pour les sous-domaines extra (palier 8).
 *
 * **Aucun n’est retenu.** Ne pas acheter, ne pas câbler Route 53, ne pas
 * afficher ces noms comme une offre. Quand le choix sera fait, remplir cette
 * table — `typedSubdomainPreview` redeviendra une vraie adresse.
 *
 * Voir `docs/lots.md` → « Domaines métier pour sous-domaines boutique ».
 */
export const TYPED_DOMAIN_ROOTS: Readonly<Partial<Record<ShopTemplateId, string>>> = {};

export function typedDomainRoot(template: ShopTemplateId): string | null {
  return TYPED_DOMAIN_ROOTS[template] ?? null;
}

export function typedSubdomainPreview(slug: string, template: ShopTemplateId): string | null {
  const root = typedDomainRoot(template);
  return root === null ? null : `${slug}.${root}`;
}

function blankToNull(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function isCustomHost(value: string): boolean {
  return (
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/u.test(value) &&
    value.includes('.') &&
    !value.endsWith('.')
  );
}
